import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useInvoices } from '../../context/InvoiceContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { formatCurrency } from '../../utils/format';
import { genRequest } from '../../utils/request';
import Breadcrumb from '../common/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import SendMailModal from '../common/SendMailModal';
import { 
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  MapPinIcon,
  ArrowLeftIcon,
  PencilIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  EllipsisVerticalIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

const InvoiceView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {getInvoice, getInvoiceById } = useInvoices();
  const { showLoading, hideLoading } = useLoading();
  const getRoutePath = useRoutePath();
  const { getToken } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Refs to prevent API loops
  const hasFetchedInvoice = useRef(false);

  // Load invoice data
  const fetchInvoiceData = useCallback(async () => {
    if (!id || hasFetchedInvoice.current) return;
    
    hasFetchedInvoice.current = true;
    if(!getInvoice){
      showLoading();
    }
    
    try {
      const response = await getInvoiceById(id);
      if (response?.data) {
        setInvoice(response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      hideLoading();
    }
  }, [id, getInvoiceById, showLoading, hideLoading,getInvoice]);

  // Fetch data on mount
  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const data = {
      id: id,
      module: 'invoice',
      action: 'pdf'
    };
    return await genRequest(data, getToken(), showLoading, hideLoading);
  };

  const handleDownloadExcel = async () => {
    const data = {
      id: id,
      module: 'invoice',
      action:'excel'
    };
    return await genRequest(data,getToken(),showLoading,hideLoading);
  };
  
  const handleSendMail = () => {
    setShowSendMailModal(true);
  };

  if (!getInvoice) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009efb] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="container mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Invoice Details
                </h1>
                <p className="text-gray-600 mt-1 text-sm md:text-base">
                  Invoice #{invoice?.invoice_number || '-'}
                </p>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/invoices'))}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              {!invoice?.is_full_paid ? (
                <Link to={getRoutePath(`/payments/create?invoiceId=${invoice?.invoice_number}`)} className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]">
                  <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                  Receive Payment
                </Link>
              ): <>
                 
                <Link to={getRoutePath(`/payments/?invoiceId=${invoice?.invoice_number}`)} className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-success hover:bg-success-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]">
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Payments
                </Link>
              </>}

              <button
                onClick={() => navigate(getRoutePath(`/invoices/edit/${id}`))}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>

              <button
                onClick={handlePrint}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>

              <button disabled={!getInvoice || !invoice}
                  onClick={handleSendMail}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  Send Mail
              </button>

              {/* More menu */}
              <div className="relative">
  
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>

                {showMoreMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1 text-xs" role="menu">
                      <button
                        onClick={handleDownloadPDF}
                        className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                        Download PDF
                      </button>
                      <button
                        onClick={() => {
                          handleDownloadExcel();
                          setShowMoreMenu(!showMoreMenu)
                        }}
                        className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        <TableCellsIcon className="h-4 w-4 mr-3" />
                        Download as Excel
                      </button>
                       
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

         
        {invoice?.is_full_paid === 1 && (
          <div className="flex items-center my-4">
            <div className="flex-grow border-t border-green-500"></div>

            <div className="mx-4 flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-500 rounded-full">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-green-700 font-semibold uppercase tracking-wide">
                Paid in Full
              </span>
            </div>

            <div className="flex-grow border-t border-green-500"></div>
          </div>
        )}


        {/* Bill To and Bill From Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* Bill To Card */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="px-6 py-4 border-b border-gray-200 bg-blue-100">
              <h2 className="text-lg font-semibold text-gray-900">Bill To</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  {invoice?.job?.customer?.id ? (
                    <Link 
                      to={getRoutePath(`/customers/view/${invoice?.job?.customer?.id}`)} 
                      className="text-lg font-bold text-[#009efb] hover:text-[#0089db]"
                      target="_blank"
                    >
                      {invoice?.job?.customer?.name || '-'}
                    </Link>
                  ) : (
                    <p className="text-lg font-bold text-gray-900">
                      {invoice?.job?.customer?.name || '-'}
                    </p>
                  )}
                </div>
                
                {invoice?.job?.customer?.service_locations?.[0] && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Service Address
                    </label>
                    <div className="flex items-start text-sm text-gray-900">
                      <MapPinIcon className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p>{invoice?.job?.customer?.service_locations?.[0]?.street_address || '-'}</p>
                        
                      </div>
                    </div>
                  </div>
                )}
                
                {invoice?.job?.customer?.p_emails?.[0]?.address && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900">
                      {invoice?.job?.customer?.p_emails[0]?.address}
                    </p>
                  </div>
                )}
                
                {invoice?.job?.customer?.p_phone_numbers?.[0]?.number && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Phone
                    </label>
                    <p className="text-sm text-gray-900">
                      {invoice?.job?.customer?.p_phone_numbers[0]?.number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bill From Card */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <h2 className="text-lg font-semibold text-gray-900">Bill From</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {invoice?.company?.name || '-'}
                  </p>
                </div>
                
                {(invoice?.company?.address1 || invoice?.company?.address2) && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Address
                    </label>
                    <div className="flex items-start text-sm text-gray-900">
                      <MapPinIcon className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        {invoice?.company?.address1 && <p>{invoice.company.address1}</p>}
                        {invoice?.company?.address2 && <p>{invoice.company.address2}</p>}
                        {(invoice?.company?.city || invoice?.company?.state || invoice?.company?.zip_code) && (
                          <p>
                            {invoice?.company?.city && `${invoice.company.city}, `}
                            {invoice?.company?.state && `${invoice.company.state} `}
                            {invoice?.company?.zip_code || ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {invoice?.company?.email && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900">
                      {invoice?.company?.email}
                    </p>
                  </div>
                )}
                
                {invoice?.company?.phone && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Phone
                    </label>
                    <p className="text-sm text-gray-900">
                      {invoice?.company?.phone}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Invoice Information Card */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Invoice Information
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Invoice Number
                    </label>
                    <p className="text-base font-semibold text-gray-900">
                      {invoice?.invoice_number || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Invoice Date
                    </label>
                    <div className="flex items-center text-base text-gray-900">
                      <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                      {invoice?.formated_invoice_date || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Payment Terms
                    </label>
                    <p className="text-base text-gray-900">
                      {invoice?.payment_term?.name || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Status
                    </label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                      invoice?.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {invoice?.active ? (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inactive
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Job Information Card */}
            {invoice?.job && (
              <div className="bg-white rounded-lg shadow-md">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                    Associated Job
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Job Number
                      </label>
                      <Link 
                        to={getRoutePath(`/jobs/view/${invoice?.job?.id}`)} 
                        className="text-base font-semibold text-[#009efb] hover:text-[#0089db]"
                        target="_blank"
                      >
                        {invoice?.job?.job_number || '-'}
                      </Link>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Job Date
                      </label>
                      <div className="flex items-center text-base text-gray-900">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {invoice?.job?.formated_created_at || '-'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        PO Number
                      </label>
                      <p className="text-base text-gray-900">
                        {invoice?.job?.po_number || '-'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Location
                      </label>
                      <div className="flex items-start text-base text-gray-900">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-400 mt-1 flex-shrink-0" />
                        <span>
                            {invoice?.job?.customer?.service_locations?.[0]?.street_address || '-'}
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Description
                      </label>
                      <p className="text-base text-gray-900">
                        {invoice?.job?.description || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Information Card */}
            {invoice?.job?.customer && (
              <div className="bg-white rounded-lg shadow-md">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                    Customer Information
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Customer Name
                      </label>
                      <Link 
                        to={getRoutePath(`/customers/view/${invoice?.job?.customer?.id}`)} 
                        className="text-base font-semibold text-[#009efb] hover:text-[#0089db]"
                        target="_blank"
                      >
                        {invoice?.job?.customer?.name || '-'}
                      </Link>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Email
                      </label>
                      <p className="text-base text-gray-900">
                        {invoice?.job?.customer?.p_emails[0]?.address || '-'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Phone
                      </label>
                      <p className="text-base text-gray-900">
                        {invoice?.job?.customer?.p_phone_numbers[0]?.number || '-'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Company
                      </label>
                      <p className="text-base text-gray-900">
                        
                        {invoice?.company?.name || '-'}
                       
                      </p>
                      <p className="text-sm text-gray-500">{invoice?.company?.address1 || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Financial Summary */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Financial Summary Card */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Financial Summary
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Job Total</span>
                  <span className="text-base font-semibold text-gray-900">
                    {formatCurrency(invoice?.job?.job_total || 0)}
                  </span>
                </div>

                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Deposits Paid</span>
                  <span className="text-base font-semibold text-green-600">
                    {formatCurrency(invoice?.job?.total_payments_deposits_cost || 0)}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-2 bg-blue-50 px-4 py-3 rounded-lg">
                  <span className="text-base font-bold text-gray-900">Total Due</span>
                  <span className="text-xl font-bold text-[#009efb]">
                    {formatCurrency(invoice?.job?.total_due || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Timeline Card */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Timeline
                </h2>
              </div>
              <div className="p-6 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Created At
                  </label>
                  <p className="text-sm text-gray-900">
                    {invoice?.formated_created_at || '-'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Last Updated
                  </label>
                  <p className="text-sm text-gray-900">
                    {invoice?.formated_updated_at || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

       {/* Send Mail Modal */}
       <SendMailModal
        isOpen={showSendMailModal}
        onClose={() => setShowSendMailModal(false)}
        moduleData={invoice}
        module="invoice"
        
      />
    </div>
  );
};

export default InvoiceView;

